import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { CheckCircle2, XCircle, Loader2, Mail, Building2, ShieldCheck } from 'lucide-react';
import { formatApiError, extractApiErrorCode } from '../../lib/api';

const API = process.env.REACT_APP_BACKEND_URL;

// Public — accept an invitation with a chosen password, then bounce to /login.
// The invitation token comes from the URL, is single-use and expires in 7 days.
export default function AcceptInvitationPage() {
    const { t } = useTranslation();
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const token = params.get('token') || '';

    // Loading / dead / ready — we only render the form after the backend
    // confirms the token is still valid (avoids letting the user type a
    // password before we know the link is dead).
    const [phase, setPhase] = useState('loading');
    const [meta, setMeta] = useState(null);
    const [error, setError] = useState('');

    const [name, setName] = useState('');
    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (!token) { setPhase('dead'); return; }
            try {
                const res = await axios.get(`${API}/api/auth/invitation/${encodeURIComponent(token)}`);
                if (cancelled) return;
                setMeta(res.data);
                setPhase('ready');
            } catch (err) {
                if (cancelled) return;
                const code = extractApiErrorCode(err);
                setError(code
                    ? t(`auth.errors.${code}`, { defaultValue: formatApiError(err) })
                    : formatApiError(err));
                setPhase('dead');
            }
        })();
        return () => { cancelled = true; };
    }, [token, t]);

    const passwordValid = useMemo(() => password.length >= 12, [password]);
    const passwordsMatch = useMemo(() => password === confirm && confirm.length > 0, [password, confirm]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!passwordValid || !passwordsMatch || submitting) return;
        setSubmitting(true);
        setError('');
        try {
            await axios.post(`${API}/api/auth/invitation/accept`, {
                token, password, password_confirm: confirm, name,
            });
            toast.success(t('accept_invitation.success_toast'));
            navigate('/login', { replace: true, state: { flash: 'invitation_accepted' } });
        } catch (err) {
            const code = extractApiErrorCode(err);
            setError(code
                ? t(`auth.errors.${code}`, { defaultValue: formatApiError(err) })
                : formatApiError(err));
            // If the token was consumed between GET and POST, kick to the
            // dead state so the invitee is not stuck retrying forever.
            if (code === 'invitation_invalid_or_used') setPhase('dead');
        } finally {
            setSubmitting(false);
        }
    };

    if (phase === 'loading') {
        return (
            <Shell>
                <div className="w-16 h-16 rounded-full bg-[#EEF2FF] flex items-center justify-center mx-auto mb-6">
                    <Loader2 className="w-8 h-8 text-[#002FA7] animate-spin" />
                </div>
                <h1 className="text-2xl font-bold text-center text-[#0A0A0A] mb-2 font-['Outfit']">
                    {t('accept_invitation.loading_title')}
                </h1>
                <p className="text-center text-[#4B5563]">{t('accept_invitation.loading_body')}</p>
            </Shell>
        );
    }

    if (phase === 'dead') {
        return (
            <Shell testId="accept-invitation-dead">
                <div className="w-16 h-16 rounded-full bg-[#FEE2E2] flex items-center justify-center mx-auto mb-6">
                    <XCircle className="w-9 h-9 text-[#DC2626]" />
                </div>
                <h1 className="text-2xl font-bold text-center text-[#0A0A0A] mb-3 font-['Outfit']">
                    {t('accept_invitation.dead_title')}
                </h1>
                <p className="text-center text-[#4B5563] mb-6">
                    {error || t('auth.errors.invitation_invalid_or_used')}
                </p>
                <Link to="/login" data-testid="accept-invitation-dead-login">
                    <Button variant="outline" className="w-full">
                        {t('accept_invitation.back_to_login')}
                    </Button>
                </Link>
            </Shell>
        );
    }

    // phase === 'ready'
    return (
        <Shell testId="accept-invitation-form">
            <div className="w-16 h-16 rounded-full bg-[#D1FAE5] flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-9 h-9 text-[#059669]" />
            </div>
            <h1 className="text-2xl font-bold text-center text-[#0A0A0A] mb-2 font-['Outfit']">
                {t('accept_invitation.title')}
            </h1>
            <p className="text-center text-[#4B5563] mb-6">
                {t('accept_invitation.subtitle', { org: meta?.organization_name || '' })}
            </p>

            <div className="rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] p-4 mb-6 space-y-2 text-sm">
                <div className="flex items-center gap-2 text-[#0A0A0A]" data-testid="accept-invitation-email">
                    <Mail className="w-4 h-4 text-[#6B7280]" />
                    <span className="font-medium break-all">{meta?.email}</span>
                </div>
                {meta?.organization_name && (
                    <div className="flex items-center gap-2 text-[#0A0A0A]" data-testid="accept-invitation-org">
                        <Building2 className="w-4 h-4 text-[#6B7280]" />
                        <span>{meta.organization_name}</span>
                    </div>
                )}
                <div className="flex items-center gap-2 text-[#0A0A0A]" data-testid="accept-invitation-role">
                    <ShieldCheck className="w-4 h-4 text-[#6B7280]" />
                    <span>{meta?.role_label || meta?.role}</span>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <Label htmlFor="accept-name">{t('accept_invitation.name_label')}</Label>
                    <Input
                        id="accept-name"
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder={t('accept_invitation.name_placeholder')}
                        autoComplete="name"
                        data-testid="accept-invitation-name-input"
                    />
                </div>
                <div>
                    <Label htmlFor="accept-password">{t('accept_invitation.password_label')}</Label>
                    <Input
                        id="accept-password"
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        minLength={12}
                        autoComplete="new-password"
                        data-testid="accept-invitation-password-input"
                    />
                    <p className={`mt-1 text-xs ${passwordValid ? 'text-[#059669]' : 'text-[#6B7280]'}`}>
                        {t('accept_invitation.password_hint')}
                    </p>
                </div>
                <div>
                    <Label htmlFor="accept-confirm">{t('accept_invitation.confirm_label')}</Label>
                    <Input
                        id="accept-confirm"
                        type="password"
                        value={confirm}
                        onChange={(e) => setConfirm(e.target.value)}
                        required
                        autoComplete="new-password"
                        data-testid="accept-invitation-confirm-input"
                    />
                    {confirm && !passwordsMatch && (
                        <p className="mt-1 text-xs text-[#DC2626]" data-testid="accept-invitation-mismatch">
                            {t('accept_invitation.mismatch')}
                        </p>
                    )}
                </div>

                {error && (
                    <div className="p-3 rounded-lg bg-[#FEE2E2] border border-[#EF4444] text-sm text-[#B91C1C]" data-testid="accept-invitation-error">
                        {error}
                    </div>
                )}

                <Button
                    type="submit"
                    disabled={!passwordValid || !passwordsMatch || submitting}
                    className="w-full bg-[#002FA7] hover:bg-[#001F70]"
                    data-testid="accept-invitation-submit-btn"
                >
                    {submitting ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('accept_invitation.submitting')}</>
                    ) : t('accept_invitation.submit')}
                </Button>
            </form>
        </Shell>
    );
}

function Shell({ children, testId }) {
    return (
        <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB] px-4 py-12" data-testid={testId || 'accept-invitation-page'}>
            <div className="max-w-md w-full bg-white p-8 rounded-2xl border border-[#E5E7EB] shadow-sm">
                {children}
            </div>
        </div>
    );
}
